import React from 'react';
import { Trophy, Zap, Cpu, Gauge, CheckCircle2, ExternalLink, ArrowLeft } from 'lucide-react';

const VOTE_URL = 'https://app.emergent.sh/showcase/builder-apr26/4d05d7f7-3dd4-4589-9208-2a9e08212e82';

export default function VoteSharePage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-['Outfit'] overflow-hidden">
      {/* Back link */}
      <div className="absolute top-4 left-4 z-10">
        <a
          href="/"
          data-testid="vote-back-link"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#111111] border border-[#27272A] text-xs text-zinc-400 hover:text-white hover:border-[#FF5E1A]/50 transition-all"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to site
        </a>
      </div>

      {/* Card — sized for 1200x675 social screenshot */}
      <section
        className="relative w-full max-w-[1200px] mx-auto aspect-[1200/675] flex flex-col justify-center px-10 sm:px-14 py-10"
        data-testid="vote-share-card"
      >
        {/* Background glow */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#FF5E1A]/20 blur-[140px]" />
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,122,255,0.04) 0px, rgba(0,122,255,0.04) 1px, transparent 1px, transparent 3px)' }} />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          {/* Left — message */}
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF5E1A]/10 border border-[#FF5E1A]/40 rounded-sm mb-5">
              <Trophy className="w-3.5 h-3.5 text-[#FF5E1A]" />
              <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A]">
                Emergent Builder Contest — April 2026
              </span>
            </div>

            <h1 className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-5">
              An AI that tunes<br />
              <span className="bg-gradient-to-r from-[#FF5E1A] via-[#FF9A42] to-[#FFD466] bg-clip-text text-transparent">
                YOUR ECU.
              </span>
              <br />Not a database.
            </h1>

            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed mb-7 max-w-xl">
              Upload your binary. AI maps it from scratch. Drive, log, upload — AI scores every pull and
              iterates until YOU Green-Light the tune.
            </p>

            <a
              href={VOTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="vote-cta-primary"
              className="inline-flex items-center gap-2 px-7 py-4 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all shadow-[0_0_40px_rgba(255,94,26,0.45)]"
            >
              Vote for AI Tuner Pro <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-[11px] text-zinc-500 mt-3 font-['JetBrains_Mono']">
              10 seconds · no sign-up · one click
            </p>
          </div>

          {/* Right — proof grid */}
          <div className="lg:col-span-2 space-y-3">
            {[
              { icon: Gauge,   title: '289 HP verified',    sub: 'BMW N51 · V9 tune · 3rd-gear pull' },
              { icon: Cpu,     title: 'AI Discovery Mode',  sub: 'Works on unknown ECUs — BMW, GM, Ford, more' },
              { icon: Zap,     title: 'Green Light loop',   sub: 'Unlimited revisions. You approve, not the shop.' },
              { icon: CheckCircle2, title: 'Built from scratch', sub: 'One founder. Real code. Real datalogs.' },
            ].map((b) => (
              <div key={b.title} className="p-3 bg-[#111111] border border-[#27272A] rounded-sm flex items-start gap-3">
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-sm bg-[#FF5E1A]/10 border border-[#FF5E1A]/30">
                  <b.icon className="w-4 h-4 text-[#FF5E1A]" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-white">{b.title}</div>
                  <div className="text-[11px] text-zinc-400 leading-tight">{b.sub}</div>
                </div>
              </div>
            ))}

            <div className="pt-1 text-right">
              <div className="font-black text-xl tracking-tighter">AI TUNER PRO</div>
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-500">
                aitunerproportal.com
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Below-the-fold share helpers (not part of the 1200x675 card) */}
      <section className="max-w-3xl mx-auto px-6 py-10 border-t border-[#27272A]">
        <div className="text-center mb-2">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-2">
            // Help us win — repost to your page
          </div>
          <p className="text-sm text-zinc-400 max-w-xl mx-auto mb-6">
            One click posts the vote link straight to your X, Facebook, or a text message. No copy-paste needed.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Help a solo builder win the Emergent Builder Contest! I\'ve been using his AI ECU tuning portal — it actually works from YOUR binary, not a shared database. 10-sec vote:')}&url=${encodeURIComponent(VOTE_URL)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-twitter"
            className="p-4 bg-[#111111] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 transition-all text-center group"
          >
            <div className="font-bold text-sm text-white mb-1 group-hover:text-[#FF5E1A] transition-colors">Post to X / Twitter</div>
            <div className="text-[11px] text-zinc-500">Pre-filled tweet</div>
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(VOTE_URL)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-facebook"
            className="p-4 bg-[#111111] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 transition-all text-center group"
          >
            <div className="font-bold text-sm text-white mb-1 group-hover:text-[#FF5E1A] transition-colors">Post to Facebook</div>
            <div className="text-[11px] text-zinc-500">Group or page friendly</div>
          </a>
          <a
            href={`sms:?&body=${encodeURIComponent('Check this out — my buddy\'s AI tuning app is up for an Emergent Builder award. One-click vote: ' + VOTE_URL)}`}
            data-testid="share-sms"
            className="p-4 bg-[#111111] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 transition-all text-center group"
          >
            <div className="font-bold text-sm text-white mb-1 group-hover:text-[#FF5E1A] transition-colors">Text to a friend</div>
            <div className="text-[11px] text-zinc-500">One-tap SMS</div>
          </a>
        </div>

        {/* Extra: LinkedIn, Reddit, WhatsApp, Email */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(VOTE_URL)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-linkedin"
            className="p-3 bg-[#0F0F0F] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 text-center text-xs text-zinc-300 hover:text-[#FF5E1A] transition-all"
          >LinkedIn</a>
          <a
            href={`https://www.reddit.com/submit?url=${encodeURIComponent(VOTE_URL)}&title=${encodeURIComponent('AI ECU Tuning app — up for an Emergent Builder award')}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-reddit"
            className="p-3 bg-[#0F0F0F] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 text-center text-xs text-zinc-300 hover:text-[#FF5E1A] transition-all"
          >Reddit</a>
          <a
            href={`https://wa.me/?text=${encodeURIComponent('Quick favor — vote for my friend\'s AI tuning app: ' + VOTE_URL)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-whatsapp"
            className="p-3 bg-[#0F0F0F] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 text-center text-xs text-zinc-300 hover:text-[#FF5E1A] transition-all"
          >WhatsApp</a>
          <a
            href={`mailto:?subject=${encodeURIComponent('Vote for AI Tuner Pro — Emergent Builder Contest')}&body=${encodeURIComponent('My friend built an AI that tunes your ECU from your own binary. He\'s up for an Emergent Builder award — one-click vote:\n\n' + VOTE_URL)}`}
            data-testid="share-email"
            className="p-3 bg-[#0F0F0F] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 text-center text-xs text-zinc-300 hover:text-[#FF5E1A] transition-all"
          >Email</a>
        </div>

        {/* Copy-paste link */}
        <CopyLinkPanel url={VOTE_URL} />
      </section>
    </div>
  );
}

function CopyLinkPanel({ url }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be blocked in private mode — let the user select manually
    }
  };
  return (
    <div className="mt-6 p-4 bg-[#0F0F0F] border border-[#27272A] rounded-sm">
      <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-500 mb-2">
        // Or copy-paste the link anywhere
      </div>
      <div className="flex items-stretch gap-2 flex-wrap">
        <code className="flex-1 min-w-0 text-xs text-[#FF5E1A] font-['JetBrains_Mono'] break-all select-all bg-black/40 px-3 py-2 rounded-sm border border-[#27272A]" data-testid="vote-raw-url">
          {url}
        </code>
        <button
          onClick={handleCopy}
          data-testid="copy-vote-url-btn"
          className={`px-4 py-2 rounded-sm text-xs font-bold tracking-[0.1em] uppercase transition-all ${
            copied
              ? 'bg-emerald-500 text-black'
              : 'bg-[#FF5E1A] hover:bg-[#FF7A42] text-white'
          }`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
